import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode } from 'react';
import askAiOff from '../../../../assets/alison-run/ask-ai-off.svg';
import askAiOn from '../../../../assets/alison-run/ask-ai-on.svg';
import closeX from '../../../../assets/alison-run/close-x.svg';
import circleLarge from '../../../../assets/alison-run/intro-circle-1.svg';
import circleSmall from '../../../../assets/alison-run/intro-circle-2.svg';
import sparkleLarge from '../../../../assets/alison-run/sparkle-1.svg';
import sparkleSmall from '../../../../assets/alison-run/sparkle-2.svg';
import { useAlisonRun } from '../AlisonRunProvider.js';
import { fillTemplate, recommendedSteps } from '../data/alisonRun.js';
import {
  ALISON_BORDER_GRADIENT,
  ALISON_FLOATING_SHADOW,
  ALISON_INTRO_CTA_GRADIENT,
  ALISON_TEXT_SUBTLE,
} from './alisonRunColors.js';

const cardIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;
const twinkle = keyframes`
  0% { opacity: 0; transform: scale(0.4); }
  60% { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;
const pillIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;
const CARD_MS = 250;
const STAGE_STAGGER_MS = 60;

export function AlisonIntroCard(): ReactNode {
  const { introOpen, rows, cards, copy, closeIntro, begin } = useAlisonRun();
  if (!introOpen || rows.length === 0) return null;
  // A goal's route or the longest-route fallback is not Alison's recommendation.
  const recommended =
    rows.length > 1 &&
    recommendedSteps(cards, [rows[0]?.actionType ?? '']).join() ===
      rows
        .slice(1)
        .map((row) => row.actionType)
        .join();
  const first = rows[0]?.title ?? '';
  const last = rows[rows.length - 1]?.title ?? '';

  return (
    <Box
      role="dialog"
      aria-label={copy.title}
      sx={{
        position: 'fixed',
        right: '29px',
        bottom: '28px',
        boxSizing: 'border-box',
        width: 680,
        maxWidth: 'calc(100vw - 16px)',
        overflow: 'hidden',
        zIndex: (theme) => theme.zIndex.modal,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        pl: '11px',
        pr: '19px',
        pt: '5px',
        pb: '6px',
        border: '1px solid transparent',
        borderRadius: '32px',
        background: (theme) =>
          `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}) padding-box, ${ALISON_BORDER_GRADIENT} border-box`,
        boxShadow: ALISON_FLOATING_SHADOW,
        transformOrigin: 'bottom right',
        animation: `${cardIn} ${CARD_MS}ms ease-out both`,
      }}
    >
      <Box
        component="img"
        src={circleSmall}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          left: '-260.993px',
          top: '18.507px',
          width: 716.987,
          height: 716.987,
          maxWidth: 'none',
          pointerEvents: 'none',
        }}
      />
      <Box
        component="img"
        src={circleLarge}
        alt=""
        aria-hidden
        sx={{
          position: 'absolute',
          left: '90.025px',
          top: '-313.475px',
          width: 750.95,
          height: 750.95,
          maxWidth: 'none',
          pointerEvents: 'none',
        }}
      />
      <Box
        component="img"
        src={sparkleLarge}
        alt=""
        aria-hidden
        sx={{ position: 'absolute', left: '290.5px', top: '90.5px', width: 7, height: 7 }}
      />
      <Box
        component="img"
        src={sparkleSmall}
        alt=""
        aria-hidden
        sx={{ position: 'absolute', left: '409.5px', top: '191.5px', width: 5, height: 5 }}
      />
      <Stack
        direction="row"
        sx={{ position: 'relative', alignItems: 'center', gap: '12px', px: '24px', py: '16px' }}
      >
        <Box component="img" src={askAiOff} alt="" sx={{ width: 32, height: 32, flexShrink: 0 }} />
        <Typography
          sx={{
            flex: 1,
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 'normal',
            color: 'background.icon',
          }}
        >
          {copy.title}
        </Typography>
        <IconButton
          aria-label={copy.close}
          onClick={closeIntro}
          sx={{ p: 0, width: 16, height: 16 }}
        >
          <Box component="img" src={closeX} alt="" sx={{ width: 16, height: 16 }} />
        </IconButton>
      </Stack>
      <Stack
        sx={{
          position: 'relative',
          alignItems: 'center',
          textAlign: 'center',
          gap: '8px',
          py: '24px',
          px: 0,
        }}
      >
        <Box
          component="img"
          src={askAiOn}
          alt=""
          sx={{
            width: 128,
            height: 128,
            display: 'block',
            animation: `${twinkle} 400ms ease-out ${CARD_MS}ms both`,
          }}
        />
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, lineHeight: '19px', color: 'background.icon' }}
        >
          {copy.introHeadline}
        </Typography>
        <Typography
          sx={{ fontSize: 12, fontWeight: 400, lineHeight: '20px', color: ALISON_TEXT_SUBTLE }}
        >
          {fillTemplate(copy.introBody, { first, last })}
        </Typography>
        {recommended && (
          <Typography
            variant="overline"
            component="p"
            sx={{ mt: '20px', color: 'primary.main', lineHeight: 'normal' }}
          >
            {copy.recommendedLabel}
          </Typography>
        )}
        <Stack
          direction="row"
          aria-label={recommended ? copy.recommendedLabel : undefined}
          sx={{
            alignItems: 'center',
            gap: '6px',
            mt: recommended ? '8px' : '20px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {rows.map((row, index) => (
            <Fragment key={row.actionType}>
              {index > 0 && (
                <Typography
                  sx={{ fontSize: 12, lineHeight: 'normal', color: 'text.muted' }}
                  aria-hidden
                >
                  →
                </Typography>
              )}
              <Box
                sx={{
                  px: '12px',
                  py: '6px',
                  borderRadius: '100px',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 'normal',
                  animation: `${pillIn} 200ms ease-out ${CARD_MS + index * STAGE_STAGGER_MS}ms both`,
                }}
              >
                {row.title}
              </Box>
            </Fragment>
          ))}
        </Stack>
      </Stack>
      <Stack
        direction="row"
        sx={{
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          p: '24px',
        }}
      >
        <Typography
          sx={{
            position: 'absolute',
            left: 0,
            top: '34px',
            fontSize: 11,
            lineHeight: 'normal',
            color: ALISON_TEXT_SUBTLE,
            whiteSpace: 'nowrap',
          }}
        >
          {copy.introFootnote}
        </Typography>
        <Button
          onClick={closeIntro}
          sx={{
            minWidth: 0,
            height: 34,
            px: '10px',
            py: '8px',
            borderRadius: '9px',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 'normal',
            color: 'text.muted',
          }}
        >
          {copy.introDismiss}
        </Button>
        <Button
          variant="contained"
          onClick={begin}
          sx={{
            minWidth: 0,
            height: 34,
            px: '12px',
            py: '8px',
            borderRadius: '9px',
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 'normal',
            background: ALISON_INTRO_CTA_GRADIENT,
            boxShadow: 'none',
            transition: 'transform 150ms ease-out',
            '&:hover': {
              background: ALISON_INTRO_CTA_GRADIENT,
              boxShadow: 'none',
              transform: 'scale(1.04)',
            },
          }}
        >
          {copy.introStart}
        </Button>
      </Stack>
    </Box>
  );
}
